import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Trash2, Plus, XCircle } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import AlertNotification from '../../components/AlertNotification.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import TradeInStepper from '../../components/TradeInStepper.js';
import Pagination from '../../components/Pagination.js';
import noImage from '../../assets/img/brand/noImage.png';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import apiHelper from '../../apiHelper.js';
import { set } from 'date-fns';

// ─── Constants ────────────────────────────────────────────────────────────────
const YELLOW = '#FFEB00';


// Accepted upload formats + size limit for the "Upload" tab
const UPLOAD_ACCEPTED_TYPES = ['image/png', 'image/webp', 'image/jpeg'];
const UPLOAD_MAX_SIZE = 1 * 1024 * 1024; // 1MB
const UPLOAD_ERROR_MESSAGE = 'Only PNG/WEBP/JPEG files below 1MB are supported';

// ─── Remove-image badge (always visible, top-right; click removes immediately) ─
function RemoveImageControl({ onRemove }) {
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            style={{
                position: 'absolute', top: 4, right: 4,
                width: 22, height: 22, borderRadius: '50%',
                background: '#E9ECEF', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 2,
            }}
        >
            <X size={12} color="#212529" />
        </button>
    );
}

// ─── Image Reselect Modal ─────────────────────────────────────────────────────
// Listing items are no longer tied to a real SKU — images come from the SPU's own images, or a manual upload.
function ImageReselectModal({ images, initialSelected, initialUploadedImages, onCancel, onConfirm }) {

    const initialIsUpload = Boolean(initialSelected?.isUploaded || initialSelected?.tab === 'tradein');
    const [activeTab, setActiveTab] = useState(initialIsUpload ? 'upload' : 'spu');
    const fileInputRef = useRef(null);
    const uploadCounterRef = useRef(0);

    // Determine which image to show when modal opens
    const resolveInitial = () => {
        if (initialIsUpload) return initialSelected;
        if (!initialSelected) {
            const first = images[0] || null;
            return first ? { ...first, tab: 'spu' } : null;
        }

        const found = images.find(s =>
            (initialSelected.id && s.id === initialSelected.id) ||
            (initialSelected.url && s.url === initialSelected.url)
        );
        if (found) return { ...found, tab: 'spu' };

        // Previously selected image has been deleted → fallback to first image
        return images[0] ? { ...images[0], tab: 'spu' } : null;
    };

    const [selected, setSelected] = useState(resolveInitial());
    // Every file ever uploaded for this row (not just the selected one) persists across
    // reopening the modal — all of them get saved to tradein_listing_item_images, not just the pick.
    const [uploadedImages, setUploadedImages] = useState(
        (initialUploadedImages || []).map(u => ({ ...u, error: null }))
    );
    // Already-persisted images (tradein_listing_item_images) removed during this session.
    // They have to be named explicitly on save: the payload otherwise only ever carries
    // brand-new files, so the backend has no way to tell a removal from a no-op.
    const [removedSavedIds, setRemovedSavedIds] = useState([]);

    const isSameImage = (a, b) => {
        if (!a || !b) return false;
        if (a.id !== null && b.id !== null) return a.id === b.id;
        return a.url === b.url;
    };

    // Clicking an image always selects it — it only changes when a *different*
    // image is picked, clicking the already-selected one again does nothing.
    const toggle = (img) => {
        setSelected({ ...img, tab: 'spu' });
    };

    const isSelected = (img) => isSameImage(selected, img);

    const toggleUploaded = (img) => {
        if (img.error) return;
        setSelected(prev => {
            if (prev?.id === img.id) return prev;
            // Already-persisted upload (from a previous save) — resolve it like any other
            // real backend image reference, no re-encoding needed.
            if (img.isSaved) return { id: img.id, url: img.preview, tab: 'tradein' };
            return { id: img.id, url: img.preview, file: img.file, name: img.name, isUploaded: true };
        });
    };

    const handleFilesSelected = (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        const newEntries = files.map(file => {
            uploadCounterRef.current += 1;
            const id = `upload-${uploadCounterRef.current}`;
            const isValid = UPLOAD_ACCEPTED_TYPES.includes(file.type) && file.size <= UPLOAD_MAX_SIZE;
            return isValid
                ? { id, file, preview: URL.createObjectURL(file), name: file.name, error: null }
                : { id, file, preview: null, name: file.name, error: UPLOAD_ERROR_MESSAGE };
        });
        setUploadedImages(prev => [...prev, ...newEntries]);
        // Auto-select the last successfully-uploaded file so "Confirm" actually uses it
        const lastValid = [...newEntries].reverse().find(e => !e.error);
        if (lastValid) {
            setSelected({ id: lastValid.id, url: lastValid.preview, file: lastValid.file, name: lastValid.name, isUploaded: true });
        }
    };

    const handleFileInputChange = (e) => {
        handleFilesSelected(e.target.files);
        e.target.value = '';
    };

    const removeUploaded = (id) => {
        const removed = uploadedImages.find(i => i.id === id);
        if (removed?.isSaved && removed.id != null) {
            setRemovedSavedIds(prev => (prev.includes(removed.id) ? prev : [...prev, removed.id]));
        }
        const remaining = uploadedImages.filter(i => i.id !== id && !i.error);
        setUploadedImages(prev => prev.filter(i => i.id !== id));
        setSelected(prev => {
            if (!prev) return prev;
            // A saved upload loaded from the backend as the row's active image carries id: null
            // and the *item's own* copied file url. That copy is written under a different name
            // than the history row it came from (both land in images/tradein/, so the second save
            // gets suffixed), so neither id nor url can match it back to its tile — there is no
            // link in the GET response between tradein_image_url and any tradein_images entry.
            // Treat that state as a hit: leaving the row pointed at an image the user just
            // deleted is what made the removal look like it was ignored.
            const ambiguous = Boolean(removed?.isSaved) && prev.tab === 'tradein' && prev.id == null;
            const isActive = prev.id === id || (removed?.preview && prev.url === removed.preview) || ambiguous;
            if (!isActive) return prev;
            // Prefer another image the user actually uploaded, so a removal stays within their own
            // pictures; fall back to the SPU's first, same as resolveInitial does when a
            // previously-picked image has since been deleted.
            const next = remaining[0];
            if (next) {
                return next.isSaved
                    ? { id: next.id, url: next.preview, tab: 'tradein' }
                    : { id: next.id, url: next.preview, file: next.file, name: next.name, isUploaded: true };
            }
            return images[0] ? { ...images[0], tab: 'spu' } : null;
        });
    };

    const tabStyle = (tab) => ({
        height: '35px',
        minWidth: 88,
        padding: '10px',
        fontSize: 12,
        fontWeight: 600,
        fontFamily: 'Montserrat',
        cursor: 'pointer',
        border: 'none',
        borderRadius: 8,
        background: activeTab === tab ? YELLOW : 'transparent',
        color: activeTab === tab ? '#212529' : '#6C757D',
    });

    return (
        <>
            <div
                className="drawer-overlay"
                onClick={onCancel}
                style={{ zIndex: 1040 }}
            />
            <div style={{
                position: 'fixed',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#fff',
                borderRadius: 8,
                boxShadow: '0px 4px 24px rgba(0,0,0,0.18)',
                zIndex: 1050,
                width: '90%',
                maxWidth: 1130,
                minHeight: '640px',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '15px 20px',
                    // borderBottom: '1px solid #E9ECEF',
                    borderRadius: '12px 12px 0 0',
                    background: '#F8F9FA',
                    flexShrink: 0,
                }}>
                    <span style={{ fontWeight: 600, fontSize: 14, fontFamily: 'Montserrat', color: '#212529' }}>
                        Select or Upload Listing Item Image<span style={{ color: '#DC3545' }}>*</span>
                    </span>
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
                    >
                        <X size={18} color="#000" />
                    </button>
                </div>


                {/* Body */}
                <div style={{ padding: '20px', flex: 1, overflowY: 'auto' }}>
                    {/* Tabs */}
                    <div style={{
                        display: 'flex', flexShrink: 0, borderRadius: '8px', padding: '10px', marginBottom: 16,
                        background: '#FFF', boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)', gap: 8,
                    }}>
                        <button type="button" style={tabStyle('spu')} onClick={() => setActiveTab('spu')}>SPU</button>
                        <button type="button" style={tabStyle('upload')} onClick={() => setActiveTab('upload')}>Upload</button>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                        <span style={{ fontSize: 12, color: '#6C757D', fontFamily: 'Montserrat' }}>
                            {activeTab === 'spu'
                                ? 'Showing all images of the SPU in ERP. Select one to use as the Listing Item Image.'
                                : 'Only PNG/WEBP/JPEG files below 1MB are supported.'}
                        </span>
                        <span style={{ fontSize: 12, color: '#6C757D', fontFamily: 'Montserrat' }}>
                            Total: {activeTab === 'spu' ? images.length : uploadedImages.length}
                        </span>
                    </div>

                    {activeTab === 'spu' ? (
                        images.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#ADB5BD', fontSize: 14, fontFamily: 'Montserrat' }}>
                                No images available.
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, padding: '10px 0px 20px 0px' }}>
                                {images.map(img => (
                                    <div
                                        key={img.id}
                                        onClick={() => toggle(img)}
                                        style={{
                                            border: `1px solid ${isSelected(img) ? YELLOW : '#CED4DA'}`,
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            aspectRatio: '1 / 1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#FFFFFF',
                                            boxSizing: 'border-box',
                                            transition: 'border-color 0.15s',
                                            width: '115px',
                                            height: '118px',
                                            padding: '10px',
                                        }}
                                    >
                                        <img src={img.url || noImage} alt={`img ${img.id}`} onError={e => { e.target.onerror = null; e.target.src = noImage; }} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, padding: '10px 0px 20px 0px' }}>
                            {/* Hidden file input — multiple files, filtered to accepted image types */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/webp,image/jpeg"
                                multiple
                                onChange={handleFileInputChange}
                                style={{ display: 'none' }}
                            />

                            {/* Upload tile */}
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                    border: '1px dashed #CED4DA',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 6,
                                    width: '115px',
                                    height: '118px',
                                    color: '#6C757D',
                                    fontSize: 12,
                                    fontFamily: 'Montserrat',
                                }}
                            >
                                <Plus size={20} />
                                Upload
                            </div>

                            {uploadedImages.map(img => {
                                const uploadedSelected = selected?.id === img.id;
                                return img.error ? (
                                    <div
                                        key={img.id}
                                        title={img.error}
                                        style={{
                                            position: 'relative',
                                            border: '1px solid #DC3545',
                                            borderRadius: 4,
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                                            background: '#F8F9FA',
                                            boxSizing: 'border-box',
                                            width: '115px',
                                            height: '118px',
                                        }}
                                    >
                                        <XCircle size={22} color="#DC3545" />
                                        <span style={{ color: '#DC3545', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 600 }}>Error</span>
                                        <RemoveImageControl onRemove={() => removeUploaded(img.id)} />
                                    </div>
                                ) : (
                                    <div
                                        key={img.id}
                                        onClick={() => toggleUploaded(img)}
                                        style={{
                                            position: 'relative',
                                            border: `1px solid ${uploadedSelected ? YELLOW : '#CED4DA'}`,
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#FFFFFF',
                                            boxSizing: 'border-box',
                                            transition: 'border-color 0.15s',
                                            width: '115px',
                                            height: '118px',
                                            padding: '10px',
                                        }}
                                    >
                                        <img src={img.preview} alt={img.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                        <RemoveImageControl onRemove={() => removeUploaded(img.id)} />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 12,
                    padding: '20px 0px',
                    margin: '0px 20px',
                    borderTop: '1px solid #E9ECEF',
                    flexShrink: 0,
                }}>
                    <button type="button" className="dark-button" onClick={onCancel}
                        style={{
                            fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12,
                            borderRadius: '8px',
                            border: '1px solid #212529',
                            background: '#FFF',
                            height: '34px',
                            minWidth: '100px',
                            padding: '10px',
                            boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                        Cancel
                    </button>
                    <button type="button" className="dark-border"
                        disabled={!selected}
                        onClick={() => onConfirm(selected, uploadedImages.filter(i => !i.error), removedSavedIds)}
                        style={{
                            fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12,
                            borderRadius: '8px',
                            border: `1px solid ${selected ? '#212529' : '#CED4DA'}`,
                            background: selected ? '#212529' : '#E9ECEF',
                            height: '34px',
                            minWidth: '100px',
                            padding: '10px',
                            color: selected ? 'white' : '#6C757D',
                            boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: selected ? 'pointer' : 'not-allowed',
                        }}>
                        Confirm
                    </button>
                </div>
            </div>
        </>
    );
}

// ─── Custom Checkbox ──────────────────────────────────────────────────────────
function CustomCheckbox({ checked, onChange, label }) {
    return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14, fontFamily: 'Montserrat', fontWeight: 400, color: '#212529', userSelect: 'none' }}>
            <div
                onClick={onChange}
                style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    border: '1.5px solid #212529',
                    background: checked ? '#FFEB00' : 'white',
                    boxShadow: checked ? '0 1px 4px 0 rgba(0, 0, 0, 0.10)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                    transition: 'background 0.15s, box-shadow 0.15s',
                }}
            >
                {checked && (
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 5.5L4.5 8.5L9.5 2.5" stroke="#212529" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </div>
            {label}
        </label>
    );
}

// ─── Color Swatch Input ───────────────────────────────────────────────────────
function ColorCodeInput({ value, onChange, error }) {
    const isValidHex = /^[0-9A-Fa-f]{0,6}$/.test(value);
    const displayColor = value && value.length === 6 ? `#${value}` : '#E9ECEF';

    return (
        <div>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                border: `1px solid ${error ? '#DC3545' : '#6C757D'}`,
                borderRadius: 6,
                overflow: 'hidden',
                height: 40,
                background: 'white',
                padding: '10px'
            }}>
                {/* Color circle */}
                <div style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: displayColor,
                    border: '1px solid #CED4DA',
                    flexShrink: 0,
                    // marginLeft: 8,
                }} />
                {/* Hash symbol */}
                <span style={{
                    padding: '0 4px 0 6px',
                    color: '#212529',
                    fontSize: 14,
                    fontFamily: 'Montserrat',
                    userSelect: 'none',
                }}>
                    #
                </span>
                {/* Hex input */}
                <input
                    type="text"
                    value={value}
                    onChange={e => {
                        const v = e.target.value.replace(/[^0-9A-Fa-f]/g, '').slice(0, 6);
                        onChange(v);
                    }}
                    maxLength={6}
                    placeholder="FFFFFF"
                    style={{
                        border: 'none',
                        outline: 'none',
                        flex: 1,
                        fontSize: 14,
                        fontFamily: 'Montserrat',
                        background: 'transparent',
                        minWidth: 0,
                        color: '#212529'

                        // paddingRight: 8,
                    }}
                />
            </div>
            {error && (
                <div style={{ fontSize: 11, color: '#DC3545', fontFamily: 'Montserrat', marginTop: 2 }}>{error}</div>
            )}
        </div>
    );
}

// ─── Clearable Text Input ─────────────────────────────────────────────────────
function ClearableInput({ value, onChange, onBlur, placeholder, error, disabled }) {
    return (
        <div>
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    onBlur={e => onBlur && onBlur(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    style={{
                        width: '100%',
                        height: 40,
                        border: `1px solid ${error ? '#DC3545' : '#212529'}`,
                        borderRadius: 8,
                        fontSize: 14,
                        fontFamily: 'Montserrat',
                        padding: '10px',
                        paddingRight: 28,
                        background: disabled ? '#F8F9FA' : 'white',
                        outline: 'none',
                        color: '#212529',
                        boxSizing: 'border-box',
                    }}
                />
                {value && !disabled && (
                    <button
                        type="button"
                        onClick={() => onChange('')}
                        style={{
                            position: 'absolute',
                            right: 6,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 2,
                            display: 'flex',
                            alignItems: 'center',
                            color: '#212529',
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M9 3L3 9M3 3L9 9" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </button>
                )}
            </div>
            {error && (
                <div style={{ fontSize: 11, color: '#DC3545', fontFamily: 'Montserrat', marginTop: 2 }}>{error}</div>
            )}
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

export default function AddEditTradeInListingStep2() {
    const history = useHistory();
    const location = useLocation();

    const listing = location.state?.listing || {};
    const isEditMode = Boolean(location.state?.isEdit);
    const restoredItems = location.state?.listingItems;
    const restoredPricingMap = location.state?.pricingMap || null;
    const restoredOriginalConditions = location.state?.originalConditions || null;

    const [items, setItems] = useState([]);
    const [skusLoading, setSkusLoading] = useState(true);
    const [rowErrors, setRowErrors] = useState({});

    // Client-only rows (added via "Add Listing Item") get an id in this form so they
    // can never collide with a real backend sku_id, and so trash/save logic can tell them apart.
    const newRowCounterRef = useRef(0);

    const makeBlankRow = () => {
        newRowCounterRef.current += 1;
        // isPlaceholder marks "this item genuinely has no image" (SPU has none either), as opposed
        // to an unresolved id — it's what tells the backend to clear the item's image rather than
        // fall back to its previous snapshot. WCM/Sheet render their own placeholder for it.
        const defaultImage = listing.spu?.images?.[0]
            ? { id: listing.spu.images[0].id ?? null, url: listing.spu.images[0].url || noImage }
            : { id: null, url: noImage, isPlaceholder: true };
        return {
            id: `new-${newRowCounterRef.current}`,
            newItemId: null,
            usedItemId: null,
            newPricing: null,
            usedPricing: null,
            storage: '',
            color: '',
            colorCode: 'ffffff',
            image: defaultImage,
            skuImages: [],
            uploadedImages: [],
            removedImageIds: [],
            conditionNew: true,
            conditionUsed: true,
            savedConditions: { new: false, used: false },
        };
    };

    // ── Load listing items: restore from Step 3, load saved items (edit mode), or start blank ──
    useEffect(() => {
        // Coming back from Step 3 — restore user-edited items directly (including an empty list)
        if (Array.isArray(restoredItems)) {
            setItems(restoredItems);
            // Reseed the counter past any restored "new-N" ids so freshly-added rows
            // never reuse an id already held by a restored row (they'd then share edits).
            let maxN = 0;
            restoredItems.forEach(r => {
                const match = typeof r.id === 'string' && r.id.match(/^new-(\d+)$/);
                if (match) maxN = Math.max(maxN, parseInt(match[1], 10));
            });
            newRowCounterRef.current = maxN;
            if (restoredOriginalConditions) {
                originalConditionsRef.current = restoredOriginalConditions;
            }
            setSkusLoading(false);
            return;
        }

        const tradeinListingId = listing.tradein_listing_id;
        if (!isEditMode || !tradeinListingId) {
            // Create mode — start with a single blank, manually-editable row
            setItems([makeBlankRow()]);
            setSkusLoading(false);
            return;
        }

        async function fetchSavedItems() {
            try {
                const itemsRes = await apiHelper(`sales/trade-in-listings/${tradeinListingId}/items/?include_draft=true`, 'GET');
                if (!itemsRes?.ok) { setSkusLoading(false); return; }

                const savedData = await itemsRes.json();
                const savedItems = Array.isArray(savedData) ? savedData : (savedData?.results || []);
                // Backend still stores New/Used as two separate item rows for the same
                // Storage+Color — group them back into one UI row per Storage+Color combo
                // (not by sku_id, which is no longer a reliable/required per-item identity).
                const savedByVariant = {};
                savedItems.forEach(si => {
                    const key = `${(si.storage || '').trim().toLowerCase()}||${(si.color || '').trim().toLowerCase()}`;
                    if (!savedByVariant[key]) savedByVariant[key] = [];
                    savedByVariant[key].push(si);
                });

                const merged = Object.keys(savedByVariant).map(key => {
                    const saved = savedByVariant[key];
                    // Only items with a non-null tradein_condition are "checked" (active).
                    // Items saved with tradein_condition: null are drafts (previously unchecked).
                    const activeItems = saved.filter(si => si.tradein_condition != null);
                    const first = saved[0];
                    const conditions = activeItems.map(s => (s.tradein_condition || '').toLowerCase());
                    // New and Used are two separate backend rows for this Storage+Color combo —
                    // keep both of their real ids (not just `first`'s) so Step 3 can send each
                    // condition's own id back to the backend. Losing either one here means a
                    // Storage/Color edit can only preserve one condition's Open ID and silently
                    // delists+recreates the other (ERP-4999).
                    const newActive  = activeItems.find(si => (si.tradein_condition || '').toLowerCase() === 'new');
                    const usedActive = activeItems.find(si => (si.tradein_condition || '').toLowerCase() === 'used');
                    const imageUrl = first.tradein_image_url || first.user_image_name || null;
                    // If a proper snapshot exists, send id=null so backend uses the
                    // snapshot fallback and never re-resolves the live source.
                    // For legacy items (user_image_name only), keep the live id so
                    // the backend can create a first-time snapshot.
                    const hasSnapshot = Boolean(first.tradein_image_url);
                    const defaultImage = listing.spu?.images?.[0]
                        ? { id: listing.spu.images[0].id ?? null, url: listing.spu.images[0].url || noImage }
                        : { id: null, url: noImage, isPlaceholder: true };
                    return {
                        // Used only as a stable client-side key for this editing session —
                        // no longer sent to the backend as sku_id.
                        id: first.tradein_listing_item_id,
                        // Real backend ids for each condition's own row — see comment above.
                        newItemId:  newActive  ? newActive.tradein_listing_item_id  : null,
                        usedItemId: usedActive ? usedActive.tradein_listing_item_id : null,
                        // Previously-saved Base Price + Deductions per condition (ERP-5008) — carried
                        // through so Step 3 can prefill them for any row whose Open ID is being
                        // preserved, instead of resetting pricing to blank on every re-edit.
                        newPricing:  newActive  ? { mode: newActive.base_price_mode,  value: newActive.base_price,  deductions: newActive.deductions  || [] } : null,
                        usedPricing: usedActive ? { mode: usedActive.base_price_mode, value: usedActive.base_price, deductions: usedActive.deductions || [] } : null,
                        storage: first.storage || '',
                        color: first.color || '',
                        colorCode: first.color_code || 'ffffff',
                        conditionNew: conditions.includes('new'),
                        conditionUsed: conditions.includes('used'),
                        image: imageUrl
                            ? { id: hasSnapshot ? null : null, url: imageUrl, tab: first.tradein_image_tab || 'spu' }
                            : defaultImage,
                        skuImages: [],
                        // Previously-uploaded images for this item (from tradein_listing_item_images) —
                        // shown as history in the Reselect modal's Upload tab. isSaved: true means no
                        // re-encoding/re-persisting is needed on the next save (only new uploads are).
                        uploadedImages: (first.tradein_images || []).map(img => ({
                            id: img.id,
                            file: null,
                            preview: img.url,
                            name: img.filename,
                            isSaved: true,
                        })),
                        // Ids from the list above that the user removes while editing — sent to the
                        // backend on save so the rows are actually deleted, not just hidden.
                        removedImageIds: [],
                        // Track which conditions were saved — determines new vs old in Step 3
                        savedConditions: {
                            new: conditions.includes('new'),
                            used: conditions.includes('used'),
                        },
                    };
                });
                // A listing must always have at least one row — seed a blank one if nothing was saved yet
                if (merged.length === 0) merged.push(makeBlankRow());
                setItems(merged);
                // Snapshot original conditions for uncheck detection
                const snap = {};
                merged.forEach(r => { snap[r.id] = { conditionNew: r.conditionNew, conditionUsed: r.conditionUsed }; });
                originalConditionsRef.current = snap;
            } catch (err) {
                console.error('Failed to fetch saved listing items:', err);
            } finally {
                setSkusLoading(false);
            }
        }
        fetchSavedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [page, setPage] = useState(1);
    const [notification, setNotification] = useState({ show: false, type: '', message: '' });
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(location.state?.isDirty || false);
    const [nextLocation, setNextLocation] = useState(null);
    const [isDiscarding, setIsDiscarding] = useState(false);

    // Uncheck-condition confirmation modal (edit mode only)
    const [uncheckConfirmOpen, setUncheckConfirmOpen] = useState(false);
    const [pendingUncheck, setPendingUncheck] = useState(null); // { rowId, field }

    // Store original conditions snapshot (set once when items load in edit mode)
    const originalConditionsRef = useRef(null); // { [skuId]: { conditionNew, conditionUsed } }

    // Image reselect modal state
    const [reselectModal, setReselectModal] = useState(null); // { rowId, skuId }

    const isDirtyRef = useRef(isDirty);
    const unblockRef = useRef(null);
    const itemsModifiedRef = useRef(false); // true if user changed item selections (invalidates step 3 pricing)
    useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

    // Navigation guard
    useEffect(() => {
        const unblock = history.block((loc) => {
            if (isDirtyRef.current && !isDiscarding) {
                setNextLocation(loc);
                setDiscardModalOpen(true);
                return false;
            }
        });
        unblockRef.current = unblock;
        return () => {
            unblock();
            unblockRef.current = null;
        };
    }, [history, isDiscarding]);

    // ── Before-unload (browser tab/window close) ──
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty && !isDiscarding) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty, isDiscarding]);

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
    };

    // ── Helpers ────────────────────────────────────────────────────────────────
    const updateItem = (id, field, value) => {
        setIsDirty(true);
        if (field === 'conditionNew' || field === 'conditionUsed') {
            itemsModifiedRef.current = true;
        }

        setItems(prev =>
            prev.map(r =>
                r.id === id ? { ...r, [field]: value } : r
            )
        );

        setRowErrors(prev => {
            const clone = { ...prev };

            if (clone[id]) {
                clone[id] = {
                    ...clone[id],
                    [field]: ''
                };
            }

            return clone;
        });
    };


    // Intercepts condition checkbox toggle — shows confirmation before unchecking a saved condition
    const handleConditionToggle = (rowId, field) => {
        const currentValue = items.find(r => r.id === rowId)?.[field];
        const isSavedCondition = isEditMode &&
            originalConditionsRef.current?.[rowId]?.[field] === true;

        if (currentValue && isSavedCondition) {
            setPendingUncheck({ rowId, field });
            setUncheckConfirmOpen(true);
        } else {
            updateItem(rowId, field, !currentValue);
        }
    };

    // ── Add / remove rows ─────────────────────────────────────────────────────
    const addBlankRow = () => {
        setItems(prev => [...prev, makeBlankRow()]);
        setIsDirty(true);
        itemsModifiedRef.current = true;
    };

    // Trash icon always removes the whole row. If it's currently checked (New/Used),
    // show the delist confirmation first — the row is only removed once confirmed.
    const removeRow = (rowId) => {
        setItems(prev => prev.filter(r => r.id !== rowId));
        setIsDirty(true);
        itemsModifiedRef.current = true;
    };

    const handleDeleteRow = (rowId) => {
        const row = items.find(r => r.id === rowId);
        const isActive = Boolean(row?.conditionNew || row?.conditionUsed);

        if (isActive) {
            setPendingUncheck({ rowId, field: 'delete' });
            setUncheckConfirmOpen(true);
        } else {
            removeRow(rowId);
        }
    };

    // ── Pagination ─────────────────────────────────────────────────────────────
    const totalPages = Math.ceil(items.length / PAGE_SIZE);
    const pagedItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // ── Validate ───────────────────────────────────────────────────────────────
    const validate = () => {
        // All rows can now be deleted (including the last one) — block moving on until
        // there's at least one to carry into Pricing.
        if (items.length === 0) {
            showNotification('error', 'Please add at least one Item');
            return false;
        }

        let hasError = false;
        const newRowErrors = {};

        // Validate all items: storage + color required; the Storage+Color combination must be unique
        // (a single row already covers both New and Used via its own checkboxes)
        const combos = {};
        items.forEach(item => {
            if (!item.storage.trim() || !item.color.trim()) return;
            const key = `${item.storage.trim().toLowerCase()}||${item.color.trim().toLowerCase()}`;
            if (!combos[key]) combos[key] = [];
            combos[key].push(item.id);
        });

        items.forEach(item => {
            const errs = {};
            if (!item.storage.trim()) errs.storage = 'Storage is required';
            if (!item.color.trim()) errs.color = 'Color is required';
            if (!errs.storage && !errs.color) {
                const key = `${item.storage.trim().toLowerCase()}||${item.color.trim().toLowerCase()}`;
                if (combos[key]?.length > 1) errs.duplicate = 'Duplicated';
            }
            if (Object.keys(errs).length > 0) {
                newRowErrors[item.id] = errs;
                hasError = true;
            }
        });

        setRowErrors(newRowErrors);

        if (hasError) {
            showNotification('error', 'Please fix the errors below');
            return false;
        }

        const anyConditionChecked = items.some(item => item.conditionNew || item.conditionUsed);
        if (!anyConditionChecked) {
            showNotification('error', 'Please check at least one Item');
            return false;
        }

        return true;
    };

    // ── Actions ────────────────────────────────────────────────────────────────
    const goToStep3 = () => {
        if (unblockRef.current) {
            unblockRef.current();
            unblockRef.current = null;
        }
        history.push(isEditMode ? '/trade-in-listing/edit-listing/pricing' : '/trade-in-listing/add-listing/pricing', {
            listing,
            listingItems: items,
            isEdit: isEditMode,
            isDirty,
            pricingMap: itemsModifiedRef.current ? null : restoredPricingMap,
            originalConditions: originalConditionsRef.current,
        });
    };

    const handleNext = () => {
        if (!validate()) return;
        goToStep3();
    };

    const handleBack = () => {
        if (unblockRef.current) {
            unblockRef.current();
            unblockRef.current = null;
        }
        const path = isEditMode ? '/trade-in-listing/edit-listing' : '/trade-in-listing/add-listing';
        history.push(path, { listing, listingItems: items, isEdit: isEditMode, isDirty, pricingMap: itemsModifiedRef.current ? null : restoredPricingMap });
    };

    const handleDiscard = () => {
        if (isDirty) setDiscardModalOpen(true);
        else history.push('/trade-in-listing');
    };

    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            setIsDiscarding(true);
            setIsDirty(false);
            setDiscardModalOpen(false);
            setTimeout(() => {
                if (nextLocation) {
                    const { pathname, state } = nextLocation.location || nextLocation;
                    const unblock = history.block(() => null);
                    history.push(pathname, state);
                    setTimeout(() => unblock(), 0);
                    setNextLocation(null);
                } else {
                    history.push('/trade-in-listing', { successMessage: 'Listing Discarded Successfully' });
                }
            }, 100);
        } else {
            setDiscardModalOpen(false);
            setNextLocation(null);
        }
    };

    // ── Blur validation (all rows) ────────────────────────────────────────────
    const handleBlur = (rowId, field, currentValue) => {
        const messages = { storage: 'Storage is required', color: 'Color is required' };
        const isEmpty = !currentValue?.trim();
        setRowErrors(prev => {
            const rowErrs = { ...(prev[rowId] || {}) };
            if (isEmpty) rowErrs[field] = messages[field];
            else delete rowErrs[field];
            return { ...prev, [rowId]: rowErrs };
        });
    };

    // ── Image reselect ────────────────────────────────────────────────────────
    const openReselect = (rowId) => setReselectModal({ rowId });

    const confirmReselect = (img, uploads, removedSavedIds) => {
        if (img) updateItem(reselectModal.rowId, 'image', img);
        updateItem(reselectModal.rowId, 'uploadedImages', uploads || []);
        // Accumulate across reopens of the modal — each session only reports its own removals,
        // but every one of them still has to reach the backend on the eventual save.
        if (removedSavedIds?.length) {
            const existing = items.find(r => r.id === reselectModal.rowId)?.removedImageIds || [];
            updateItem(reselectModal.rowId, 'removedImageIds', [
                ...existing,
                ...removedSavedIds.filter(id => !existing.includes(id)),
            ]);
        }
        setReselectModal(null);
    };

    // ── Render ────────────────────────────────────────────────────────────────
    const pageContainerStyle = {
        // padding: '20px 24px',
        fontFamily: 'Montserrat',
        minHeight: '100%',
        // background: '#F8F9FA',
    };

    const labelStyle = {
        fontSize: 13,
        fontWeight: 600,
        color: '#212529',
        fontFamily: 'Montserrat',
        marginBottom: 4,
        display: 'block',
    };

    // Column widths
    const COL = {
        delete: 40,
        skuName: '20%',
        conditionL1: 150,
        image: 120,
        storage: '14%',
        color: '14%',
        colorCode: 130,
    };

    const thStyle = {
        fontSize: 12,
        fontWeight: 700,
        fontFamily: 'Montserrat',
        color: '#212529',
        padding: '10px',
        background: '#E9ECEF',
        borderBottom: '1px solid #DEE2E6',
        whiteSpace: 'nowrap',
    };

    const tdStyle = (hasErr, rowBg = 'white') => ({
        padding: '10px',
        borderBottom: '1px solid #E9ECEF',
        verticalAlign: 'center',
        background: rowBg,
    });

    return (
        <div style={pageContainerStyle}>
            {/* Notification */}
            {notification.show && (
                <AlertNotification
                    type={notification.type}
                    action="create"
                    recordName="Listing"
                    message={notification.message}
                    onClose={() => setNotification(prev => ({ ...prev, show: false }))}
                />
            )}

            {/* Discard Confirmation */}
            {discardModalOpen && (
                <DiscardConfirmationModal
                    isOpen={discardModalOpen}
                    onConfirm={() => handleDiscardConfirm(true)}
                    onCancel={() => handleDiscardConfirm(false)}
                    entityName="Trade-In Listing"
                />
            )}

            {/* Uncheck Condition Confirmation Modal */}
            {uncheckConfirmOpen && (
                <>
                    <div className="drawer-overlay" />
                    <div
                        className="entity-drawer modal-popup-cls"
                        style={{
                            maxWidth: 650,
                            margin: '0 auto',
                            background: 'white',
                            borderRadius: 12,
                            zIndex: 1050,
                            padding: '20px 25px',
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
                            height: 'auto',
                            right: 'auto',
                            overflow: 'inherit',
                        }}
                    >
                        {/* Close button */}
                        <div className="d-flex justify-content-end align-items-center pt-0 pe-0">
                            <button
                                type="button"
                                onClick={() => { setUncheckConfirmOpen(false); setPendingUncheck(null); }}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', outline: 'none' }}
                                onFocus={e => e.target.blur()}
                            >
                                <svg width="18" height="18" viewBox="0 0 18 18">
                                    <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="black" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        {/* Title */}
                        <h3 style={{ textAlign: 'left', color: '#212529', fontSize: '20px', fontWeight: '600', lineHeight: 'normal', fontFamily: 'Montserrat', marginBottom: '10px', paddingTop: '20px' }}>
                            You are going to delist these Listing Items
                        </h3>
                        {/* Body */}
                        <p style={{ paddingBottom: '20px', marginBottom: 0, textAlign: 'left', color: '#6C757D', fontSize: '14px', fontWeight: '400', lineHeight: '20px', fontFamily: 'Montserrat' }}>
                            You have unchecked these Listing Items. Once saved, these Listing Items will be treated as delisted, and announce the downstream platforms.
                        </p>
                        {/* Footer */}
                        <div className="drawer-footer" style={{ paddingTop: '10px', paddingBottom: '10px' }}>
                            <button
                                type="button"
                                onClick={() => { setUncheckConfirmOpen(false); setPendingUncheck(null); }}
                                style={{ display: 'flex', height: '34px', minWidth: '100px', padding: '10px 0px', justifyContent: 'center', alignItems: 'center', gap: '10px', borderRadius: '8px', border: '1px solid #212529', background: '#FFF', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)', color: '#212529', fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (pendingUncheck) {
                                        if (pendingUncheck.field === 'delete') {
                                            removeRow(pendingUncheck.rowId);
                                        } else {
                                            updateItem(pendingUncheck.rowId, pendingUncheck.field, false);
                                        }
                                        setPendingUncheck(null);
                                    }
                                    setUncheckConfirmOpen(false);
                                }}
                                style={{ display: 'flex', height: '34px', minWidth: '100px', padding: '10px 0px', justifyContent: 'center', alignItems: 'center', gap: '10px', borderRadius: '8px', background: '#212529', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)', color: '#FFF', fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Image Reselect Modal */}
            {reselectModal && (
                <ImageReselectModal
                    images={(() => { const imgs = listing.spu?.images || []; return imgs.length > 0 ? imgs : [{ id: null, url: noImage, isPlaceholder: true }]; })()}
                    initialSelected={items.find(r => r.id === reselectModal.rowId)?.image}
                    initialUploadedImages={items.find(r => r.id === reselectModal.rowId)?.uploadedImages || []}
                    onCancel={() => setReselectModal(null)}
                    onConfirm={confirmReselect}
                />
            )}

            {/* Stepper */}
            <TradeInStepper currentStep={2} />

            {/* Page card */}
            <div style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

                {/* Page header row */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px',
                    borderBottom: '1px solid #E9ECEF',
                    background: 'white',
                    borderRadius: '8px'
                }}>
                    <h5 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'Montserrat', color: '#212529' }}>
                        Listing Items
                    </h5>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button type="button" className="btn dark-border" onClick={handleDiscard}
                            style={{
                                borderRadius: '8px',
                                background: 'none',

                                /* Shadow/Small */
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                height: '34px',
                                minWidth: '100px',
                                padding: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                                display: 'flex',
                                color: '#212529'
                            }}>
                            Discard
                        </button>
                        <button type="button" className="btn dark-border" onClick={handleBack}
                            style={{
                                borderRadius: '8px',
                                background: 'none',
                                color: '#212529',
                                /* Shadow/Small */
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                height: '34px',
                                minWidth: '100px',
                                padding: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                                display: 'flex'
                            }}>
                            Back
                        </button>
                        <button type="button" className="btn dark-button" onClick={handleNext}
                            style={{
                                borderRadius: '8px',
                                background: '#212529',

                                /* Shadow/Small */
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                height: '34px',
                                minWidth: '100px',
                                padding: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                                display: 'flex',
                                color: "white"
                            }}>
                            Next
                        </button>
                    </div>
                </div>

                {/* Subtitle */}

                {/* Table */}
                <div style={{ overflowX: 'auto', background: 'white', marginTop: '13px', borderRadius: '12px' }}>
                    <p style={{ padding: '15px 20px', borderRadius: '12px 12px 0 0', background: '#F8F9FA', fontSize: 14, color: '#212529', fontFamily: 'Montserrat', fontWeight: 600 }}>
                        Listing Item Info
                    </p>
                    <div style={{ padding: '0px 20px 20px 20px' }}>

                        {items.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-content">
                                <div className="icon">
                                    <img src={noBrandImage} alt="empty" style={{ width: 120, height: 120 }} />
                                </div>
                                <h1 className="no-item-title mt-2">No Listing Item Exists</h1>
                                <p className="no-item-text">Nothing here yet.</p>
                            </div>
                        </div>
                        ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800, marginTop: '20px' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, width: COL.delete, textAlign: 'center' }} />
                                    <th style={{ ...thStyle, width: COL.skuName }}>Listing Model</th>
                                    <th style={{ ...thStyle, width: COL.image }}>Image</th>
                                    <th style={{ ...thStyle, width: COL.storage }}>Storage</th>
                                    <th style={{ ...thStyle, width: COL.color }}>Color</th>
                                    <th style={{ ...thStyle, width: COL.colorCode }}>Color Code</th>
                                    <th style={{ ...thStyle, width: COL.conditionL1 }}>Condition</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedItems.map((row, rowIdx) => {
                                    const errs = rowErrors[row.id] || {};
                                    const hasDuplicate = Boolean(errs.duplicate);
                                    const hasRowErr = Object.keys(errs).length > 0;
                                    const rowBg = rowIdx % 2 === 0 ? 'white' : '#F8F9FA';

                                    return (
                                        <tr key={row.id}>
                                            {/* Delete row — every row can be removed, including the last one left */}
                                            <td style={{ ...tdStyle(false, rowBg), width: COL.delete, textAlign: 'center' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteRow(row.id)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex', alignItems: 'center' }}
                                                >
                                                    <Trash2 size={16} color="#212529" />
                                                </button>
                                            </td>

                                            {/* Listing Model */}
                                            <td style={{ ...tdStyle(hasRowErr, rowBg), verticalAlign: 'center' }}>
                                                <div style={{ fontSize: 12, fontFamily: 'Montserrat', color: '#212529', fontWeight: 400 }}>
                                                    {listing.listingModel}
                                                </div>
                                            </td>

                                            {/* Image */}
                                            <td style={{ ...tdStyle(false, rowBg), width: COL.image }}>
                                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                                    <div style={{
                                                        width: 32,
                                                        height: 32,
                                                        // border: '1px solid #E9ECEF',
                                                        // borderRadius: 6,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: '#FAFAFA',
                                                        overflow: 'hidden',
                                                    }}>
                                                        <img
                                                            src={row.image?.url || noImage}
                                                            alt={listing.listingModel}
                                                            onError={e => { e.target.onerror = null; e.target.src = noImage; }}
                                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => openReselect(row.id)}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            fontSize: 12,
                                                            fontFamily: 'Montserrat',
                                                            fontWeight: 600,
                                                            color: '#212529',
                                                            background: 'none',
                                                            border: '1px solid #212529',
                                                            borderRadius: 8,
                                                            padding: '10px',
                                                            cursor: 'pointer',
                                                            minWidth: '70px',
                                                            height: '34px',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        Reselect
                                                    </button>
                                                </div>
                                            </td>

                                            {/* Storage */}
                                            <td style={tdStyle(Boolean(errs.storage || hasDuplicate), rowBg)}>
                                                <ClearableInput
                                                    value={row.storage}
                                                    onChange={val => updateItem(row.id, 'storage', val)}
                                                    onBlur={(val) => handleBlur(row.id, 'storage', val)}
                                                    // placeholder="e.g. 128GB"
                                                    error={errs.duplicate ? errs.duplicate : errs.storage}
                                                />
                                            </td>

                                            {/* Color */}
                                            <td style={tdStyle(Boolean(errs.color || hasDuplicate), rowBg)}>
                                                <ClearableInput
                                                    value={row.color}
                                                    onChange={val => updateItem(row.id, 'color', val)}
                                                    onBlur={(val) => handleBlur(row.id, 'color', val)}
                                                    // placeholder="e.g. Black"
                                                    error={errs.duplicate ? errs.duplicate : errs.color}
                                                />
                                            </td>

                                            {/* Color Code */}
                                            <td style={{ ...tdStyle(false, rowBg), width: COL.colorCode, padding: '10px' }}>
                                                <ColorCodeInput
                                                    value={row.colorCode}
                                                    onChange={val => updateItem(row.id, 'colorCode', val)}
                                                    error={errs.colorCode}
                                                />
                                            </td>

                                            {/* Condition — New / Used */}
                                            <td style={{ ...tdStyle(false, rowBg), width: COL.conditionL1 }}>
                                                <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
                                                    <CustomCheckbox
                                                        checked={row.conditionNew}
                                                        onChange={() => handleConditionToggle(row.id, 'conditionNew')}
                                                        label="New"
                                                    />
                                                    <CustomCheckbox
                                                        checked={row.conditionUsed}
                                                        onChange={() => handleConditionToggle(row.id, 'conditionUsed')}
                                                        label="Used"
                                                    />
                                                </div>
                                            </td>

                                        </tr>
                                    );
                                })}

                                {pagedItems.length === 0 && (
                                    <tr>
                                        <td colSpan={7} style={{ textAlign: 'center', padding: '40px 0', color: '#ADB5BD', fontSize: 14, fontFamily: 'Montserrat' }}>
                                            No Listing Items added yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        )}

                        {/* Add Listing Item */}
                        <button
                            type="button"
                            onClick={addBlankRow}
                            style={{
                                marginTop: 16,
                                borderRadius: '8px',
                                background: 'none',
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                height: '34px',
                                minWidth: '140px',
                                padding: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                                fontFamily: 'Montserrat',
                                display: 'flex',
                                color: '#212529',
                                border: '1px solid #212529',
                                cursor: 'pointer',
                            }}
                        >
                            Add Listing Item
                        </button>
                    </div>

                    {/* Pagination */}
                    {items.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end',padding: "0 20px 20px 20px", }}>
                                         <span style={{ fontSize: '12px', fontWeight: 400, color: '#212529', fontFamily: 'Montserrat, sans-serif' }}>
                                            {(page - 1) * PAGE_SIZE + 1} - {(page - 1) * PAGE_SIZE + pagedItems.length} of {items.length}
                                        </span>
                                        <Pagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            onPageChange={setPage}
                                        />
                                    </div>
                    )}
                </div>


            </div>
        </div>
    );
}